/**
 * Common utilities for layout renderers
 */

import {type MediaCardType} from '@wandb/weave/common/types/media';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {type MediaConfigWithDefaults} from '../../Component';
import {getFitSettingsForMediaType} from '../../sizing/getFitSettingsForMediaType';
import {getMaskOptions} from '../../tiles/getTilesWithMaskOptions';
import {LAYOUT_CONSTANTS} from '../constants';
import {type GridCell, type GridRow} from '../types';

/**
 * Helper to expand cells with mask options into multiple tiles.
 *
 * @param cells - Array of grid cells to expand
 * @param config - Media config
 * @param allMasks - Available masks data
 * @returns Array of cell arrays - each original cell becomes an array of cells (one per mask)
 */
export function expandCellsWithMasks(
  cells: GridCell[],
  config: MediaConfigWithDefaults,
  allMasks?: any
): GridCell[][] {
  if (!allMasks) {
    return cells.map(cell => [cell]);
  }

  const expandedCellGroups: GridCell[][] = [];
  for (const cell of cells) {
    if (cell.isEmpty) {
      expandedCellGroups.push([cell]);
      continue;
    }

    // Get mask options for this cell
    const maskOptionsList = getMaskOptions(config, allMasks, {
      run: cell.run,
      mediaStep: cell.step,
      mediaIndex: cell.mediaIndex,
      mediaKey: cell.mediaKey,
      runSignature: cell.tileSpec.runSignature,
    });

    // If there are mask options, create a cell for each one
    // Keep them grouped together so they stack vertically in the same column
    if (maskOptionsList.length > 0) {
      const cellGroup: GridCell[] = [];
      for (let i = 0; i < maskOptionsList.length; i++) {
        const maskOpts = maskOptionsList[i];
        cellGroup.push({
          ...cell,
          maskOptions: maskOpts,
          isOverlayTile: i > 0, // First cell (index 0) is media, subsequent cells are overlays
        });
      }
      expandedCellGroups.push(cellGroup);
    } else {
      // No mask options, preserve the original cell
      expandedCellGroups.push([cell]);
    }
  }

  return expandedCellGroups;
}

/**
 * Get the className for Card.Group components with media card styling.
 *
 * @param config - Media config
 * @param mediaType - Type of media being displayed
 * @param shrunk - Whether the cards are shrunk
 * @param showActualSize - Whether to show actual size
 * @param additionalClasses - Additional Tailwind classes to include
 * @returns Merged className string
 */
export function getCardGroupClassName(
  config: MediaConfigWithDefaults,
  mediaType: MediaCardType,
  shrunk?: boolean,
  showActualSize?: boolean,
  additionalClasses?: string
): string {
  return twMerge(
    'media-cards',
    additionalClasses,
    config.pixelated && 'media-cards--pixelated',
    mediaType === 'table' && 'media-cards--table',
    shrunk && 'media-cards--shrunk',
    showActualSize && 'media-cards--actual',
    !showActualSize && 'media-cards--full'
  );
}

/**
 * Generates grid template CSS properties with optional row header and footer columns.
 *
 * @param columnCount - Number of content columns
 * @param hasRowHeaders - Whether row headers are present
 * @param hasRowFooters - Whether row footers are present
 * @param shouldLimitColumnWidth - Whether to ensure a minimum colmn width
 * @returns React.CSSProperties with gridTemplateColumns property
 */
export function getGridTemplateColumnsWithHeaders(
  columnCount: number,
  hasRowHeaders: boolean,
  hasRowFooters: boolean,
  shouldLimitColumnWidth: boolean
): React.CSSProperties {
  const headerWidth = LAYOUT_CONSTANTS.ROW_HEADER_WIDTH;
  const minColumnWidth = LAYOUT_CONSTANTS.MIN_COLUMN_WIDTH;
  const columnTemplate = shouldLimitColumnWidth
    ? `minmax(${minColumnWidth}px, 1fr)`
    : '1fr';
  return {
    gridTemplateColumns: [
      hasRowHeaders ? `minmax(${headerWidth}px, ${headerWidth}px)` : '',
      `repeat(${columnCount},${columnTemplate})`,
      hasRowFooters ? 'auto' : '',
    ]
      .filter(Boolean)
      .join(' '),
  };
}

/**
 * Generates grid template rows with optional header and footer rows.
 *
 * @param contentRowCount - Number of content rows
 * @param options - Optional row sizing behavior
 * @returns React.CSSProperties with gridTemplateRows property
 */
type GridTemplateRowsOptions = {
  contentRowFillsPanel?: boolean;
  // Caps visible rows at N via minmax(calc(100%/N), 1fr): rows fill the panel
  // when few, then hold at 1/N and scroll. N=1 means one row fills the panel.
  // Ignored if contentRowFillsPanel.
  maxVisibleRows?: number;
};

export function getGridTemplateRowsWithHeaders(
  contentRowCount: number,
  options: GridTemplateRowsOptions = {}
): React.CSSProperties {
  const contentRowTemplate = getContentRowTemplate(options);
  const rows = [`repeat(${contentRowCount}, ${contentRowTemplate})`];

  return {
    gridTemplateRows: rows.filter(Boolean).join(' '),
  };
}

// Per-row track: fill panel (100%), floored-but-growable (minmax), or even split (1fr).
function getContentRowTemplate(options: GridTemplateRowsOptions): string {
  if (options.contentRowFillsPanel === true) {
    return '100%';
  }
  if (options.maxVisibleRows != null && options.maxVisibleRows >= 1) {
    return `minmax(calc(100% / ${options.maxVisibleRows}), 1fr)`;
  }
  return '1fr';
}

/**
 * Applies pagination to grid rows (for gallery mode).
 * Flattens all rows, paginates the cells, then chunks them back into visual rows.
 *
 * @param rows - Array of grid rows to paginate
 * @param pagination - Pagination config with start and size
 * @param columnCount - Number of columns to chunk into
 * @returns Paginated rows chunked by columnCount
 */
export function applyPaginationToRows(
  rows: GridRow[],
  pagination: {start: number; size: number},
  columnCount: number
): GridRow[] {
  // Guard against invalid columnCount to prevent infinite loop
  if (columnCount <= 0) {
    return [];
  }

  // Flatten all cells
  const flatCells = rows.flatMap(row => row.cells);
  // Paginate
  const paginatedCells = flatCells.slice(
    pagination.start,
    pagination.start + pagination.size
  );
  // Chunk paginated cells into visual rows
  const paginatedRows: GridRow[] = [];
  for (let i = 0; i < paginatedCells.length; i += columnCount) {
    paginatedRows.push({
      cells: paginatedCells.slice(i, i + columnCount),
      rowKey: `paginated-row-${Math.floor(i / columnCount)}`,
    });
  }
  return paginatedRows;
}

/**
 * Helper to get mediaIndex from config.
 */
export function getMediaIndexFromConfig(
  config: MediaConfigWithDefaults
): number {
  return config.mediaIndex ?? 0;
}

/**
 * Returns `config.columnCount` after defaults were applied in `getConfigWithDefaults`.
 *
 * @param config - Media config with defaults
 * @returns Column count to use for layout and controls
 */
export function getColumnCountFromConfig(
  config: MediaConfigWithDefaults
): number {
  // Always use config.columnCount (never read from compareSettings.columnCount)
  // The value has already been clamped appropriately in useMediaPanelValues
  return config.columnCount ?? 1;
}

/**
 * Helper to check if actualSize mode is enabled for the given media type.
 *
 * actualSize means render media at its native dimensions from the server,
 * ignoring card sizing constraints. It is scoped per media type: images use
 * `config.actualSize`, videos use `config.videoActualSize`, and other media
 * types never render at actual size.
 *
 * Note: This is different from fitToDimension, which fits images within
 * card dimensions while maintaining aspect ratio.
 *
 * @param config - Media config
 * @param mediaType - Type of media being displayed
 * @returns True if actualSize mode is enabled
 */
export function getShowActualSizeFromConfig(
  config: MediaConfigWithDefaults,
  mediaType: MediaCardType
): boolean {
  return getFitSettingsForMediaType(config, mediaType).actualSize;
}

/**
 * Whether a media type fills its container's height (renders `h-full`) rather
 * than being width-sized. Height-filling media (video) need row-height management so they
 * aren't squished; width-sized media (image, object3D, etc.) don't.
 */
export function mediaTypeFillsHeight(mediaType: MediaCardType): boolean {
  return mediaType === 'video';
}

/**
 * Check if any cell in this row has captions
 *
 * @param columns - Array of columns
 * @returns True if any cell in this row has captions
 */
export function doCellsHaveCaptions(cells: GridCell[]): boolean {
  return cells.some(cell => !cell.isEmpty && cell.hasCaption);
}
